// PlayFirst Parent — Pricing & "what you get" page.
// /parent-app/pricing — PUBLIC (no auth required).
//
// Mirrors the visual language of the existing PlayFirst clinical Pricing
// section (see LandingPage.jsx PricingCard around line 1905) so the two
// products feel like one cohesive family. Two plans for now (Monthly + Annual);
// a Lifetime plan slot is left in the data table commented-out so it can be
// uncommented later without rewriting layout.
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Heart, ArrowLeft, ArrowRight, CheckCircle, Sparkles, MessageCircle,
  Map as MapIcon, Eye, Users2, Baby, Bell, ExternalLink, Package,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PartnerCodeEntry from './PartnerCodeEntry';

const PARENT_PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    monthlyPrice: 6.99,
    annualPrice: null,
    href: 'https://paystack.shop/pay/3y36h9xeh7',
    period: '/mo',
    description: 'Pause anytime. Easy in, easy out.',
    cta: 'Start Monthly',
    features: [
      'Every PlayFirst Parent feature',
      'Cancel anytime',
      'Pause or resume WhatsApp messages',
    ],
    popular: false,
  },
  {
    id: 'annual',
    name: 'Annual',
    monthlyPrice: null,
    annualPrice: 59.99,
    href: 'https://paystack.shop/pay/iq5zgoi-ii',
    period: '/yr',
    description: 'Best for parents settling in for the long play.',
    cta: 'Start Annual',
    features: [
      'Every PlayFirst Parent feature',
      'Save $24 vs paying monthly',
      'A full year of gentle nudges',
    ],
    popular: true,
  },
  // Lifetime — one-time payment, no renewals.
  {
    id: 'lifetime',
    name: 'Lifetime',
    monthlyPrice: null,
    annualPrice: null,
    oneTimePrice: 119.99,
    href: 'https://paystack.shop/pay/-0ecc21xlr',
    period: 'once',
    description: 'Pay once. Yours for the long play.',
    cta: 'Get Lifetime',
    features: [
      'Every PlayFirst Parent feature',
      'No renewals, no reminders',
      'Lifetime access for your family',
    ],
    popular: false,
  },
];

const FEATURE_GROUPS = [
  {
    icon: <Sparkles className="w-5 h-5" />,
    title: 'A gentle daily play idea',
    body: 'Personalised to how your child communicates today — gestures, sounds, scripts, signs, words. All of it counts.',
  },
  {
    icon: <Package className="w-5 h-5" />,
    title: 'Ideas for your child\u2019s actual toys',
    body: 'Add the toys (and interests) your child loves at home — snap a photo if you like — and generate a tailored play idea around each one, anchored to their Language Map.',
  },
  {
    icon: <MessageCircle className="w-5 h-5" />,
    title: 'Your WhatsApp companion',
    body: 'Daily, weekly or monthly nudges on the chat app you already use. Reply LOG to capture moments. Reply PAUSE anytime.',
  },
  {
    icon: <MapIcon className="w-5 h-5" />,
    title: 'Your child\u2019s Language Map',
    body: 'An auto-refining snapshot of how your little one communicates — and what supports their next steps.',
  },
  {
    icon: <Eye className="w-5 h-5" />,
    title: '"Where they\'re at right now"',
    body: 'A neurodiversity-affirming, age-banded view of what\u2019s often unfolding — with gentle things to notice, never a diagnosis.',
  },
  {
    icon: <Bell className="w-5 h-5" />,
    title: 'Monthly "new stage" message',
    body: 'When your child steps into a new band, we send a warm WhatsApp summary of what often unfolds next. Only when there\u2019s new news.',
  },
  {
    icon: <Users2 className="w-5 h-5" />,
    title: 'Our Collaborators',
    body: 'Curated parent courses, in-person services, and speech therapists open to early-intervention referrals. Many offer exclusive deals or freebies to PlayFirst Parent subscribers — filterable by your area.',
  },
  {
    icon: <Baby className="w-5 h-5" />,
    title: 'Every child counts',
    body: 'Add multiple children to one account. Each one gets their own play ideas, Language Map and stage view.',
  },
];

export default function ParentAppPricing() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="parent-pricing-page">
      {/* Header */}
      <header className="px-4 sm:px-8 py-4 max-w-5xl mx-auto flex items-center justify-between">
        <Link to="/parent-app" className="flex items-center gap-2 text-[#7C6F9C] hover:underline" data-testid="parent-pricing-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <Link to="/parent-app" className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-lg text-[#3a2f4f]">PlayFirst</span>
          <span className="text-[10px] uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">Parent</span>
        </Link>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 text-center pt-6 pb-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs uppercase tracking-[0.2em] text-[#7C6F9C] font-semibold mb-3">
            PlayFirst Parent
          </p>
          <h1 className="text-4xl sm:text-5xl font-serif text-[#3a2f4f] leading-tight mb-4">
            Small, gentle support — for the parents doing the actual work.
          </h1>
          <p className="text-lg text-stone-600 leading-relaxed max-w-2xl mx-auto">
            Personalised play ideas, a warm WhatsApp companion, and a quiet picture of how your child
            is communicating — from newborn onwards.
          </p>
        </motion.div>
      </section>

      {/* What you get */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-16" data-testid="parent-pricing-features">
        <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold text-center mb-6">
          What's included
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURE_GROUPS.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white/70 backdrop-blur border border-stone-200 p-5">
              <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center mb-3">
                {f.icon}
              </div>
              <h3 className="font-serif text-lg text-[#3a2f4f] mb-1.5">{f.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="plans" className="max-w-4xl mx-auto px-4 sm:px-8 pb-16" data-testid="parent-pricing-plans">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-serif text-[#3a2f4f] mb-2">Choose your plan</h2>
          <p className="text-sm text-stone-500">
            Every plan unlocks every PlayFirst Parent feature. Pick the cadence that fits your moment.
          </p>
        </div>

        {/* Partner discount code — hoisted ABOVE the plan cards so parents
            with a code see it before they click "Subscribe". The card
            reads a stored code from localStorage and persists the valid
            one for the post-signup attribution step. */}
        <div
          className="max-w-xl mx-auto mb-8 rounded-3xl bg-gradient-to-br from-amber-50 via-white to-amber-50/40 border-2 border-dashed border-amber-300/70 p-5"
          data-testid="parent-pricing-partner-code-callout"
        >
          <p className="text-center text-sm font-medium text-amber-900 mb-3">
            <span className="inline-block mr-1" aria-hidden="true">✨</span>
            Have a partner discount code? Enter it here first to unlock 30% off.
          </p>
          <PartnerCodeEntry
            redirectOnApply={true}
            testIdPrefix="parent-pricing-partner-code"
            className="!border-0 !bg-white/80"
          />
        </div>

        {/* Monthly / Annual / Lifetime — all visible. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {PARENT_PLANS.map((plan, idx) => (
            <ParentPlanCard key={plan.id} plan={plan} idx={idx} elevated={plan.popular} />
          ))}
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Payments are securely processed by Paystack. No card details are stored by PlayFirst.
        </p>
      </section>

      {/* Trust band */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-16">
        <div className="rounded-2xl border border-stone-200 bg-white/70 backdrop-blur p-6">
          <h3 className="font-serif text-lg text-[#3a2f4f] mb-2">What PlayFirst Parent isn't</h3>
          <ul className="text-sm text-stone-600 leading-relaxed space-y-1.5">
            <li>· Not a milestone tracker. We never compare your child to a "should".</li>
            <li>· Not a diagnostic tool. We surface gentle observations only.</li>
            <li>· Not a replacement for early-intervention support — we help you spot when it's worth reaching out.</li>
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-16 text-center">
        <h2 className="text-2xl font-serif text-[#3a2f4f] mb-3">Ready when you are.</h2>
        <p className="text-sm text-stone-600 mb-6">
          Choose a plan above to get started. You'll set up your child's profile right after.
        </p>
        <Link to="/parent-app/signup">
          <Button className="rounded-full bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white px-6" data-testid="parent-pricing-signup-cta">
            Create my account <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
        <p className="text-xs text-stone-400 mt-4">
          Already have an account? <Link to="/parent-app/login" className="underline">Sign in</Link>
        </p>
      </section>

      <footer className="text-center text-xs text-stone-400 pb-8">
        PlayFirst Parent is not a diagnostic, screening or emergency tool.
      </footer>
    </div>
  );
}

// Single plan card — mirrors the look + feel of the PlayFirst clinical
// PricingCard in LandingPage.jsx but in the Parent palette (#7C6F9C).
function ParentPlanCard({ plan, idx, elevated }) {
  const price = plan.annualPrice ?? plan.monthlyPrice ?? plan.oneTimePrice;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.08 }}
    >
      <Card className={`p-5 h-full rounded-2xl relative flex flex-col ${
        elevated ? 'border-2 border-[#7C6F9C] shadow-xl md:scale-[1.03] z-10' :
        plan.popular ? 'border-2 border-[#7C6F9C]/60' :
        'border-stone-200'
      }`} data-testid={`parent-pricing-card-${plan.id}`}>
        {plan.popular && (
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#7C6F9C] text-xs">
            Most popular
          </Badge>
        )}

        <div className="text-center mb-4">
          <h3 className="text-lg font-serif text-primary-900 mb-1">{plan.name}</h3>
          <div className="flex items-baseline justify-center gap-1">
            <span className="text-3xl font-bold text-primary-900">
              ${price}
            </span>
            <span className="text-stone-500 text-sm">{plan.period}</span>
          </div>
          {plan.id === 'annual' && (
            <p className="text-xs text-emerald-700 font-medium mt-1">
              Save ${(6.99 * 12 - 59.99).toFixed(2)}/yr vs Monthly
            </p>
          )}
          <p className="text-xs text-stone-600 mt-1">{plan.description}</p>
        </div>

        <ul className="space-y-2 mb-4 flex-1">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-stone-700">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
              {feature}
            </li>
          ))}
        </ul>

        <Button
          className="w-full rounded-full text-sm bg-[#7C6F9C] hover:bg-[#6a5d8a]"
          size="sm"
          asChild
          data-testid={`parent-pricing-cta-${plan.id}`}
        >
          <a href={plan.href} target="_blank" rel="noopener noreferrer">
            {plan.cta} <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </a>
        </Button>
      </Card>
    </motion.div>
  );
}
